import { useState } from "react";
import { Box, Flex, Text, Icon } from "@chakra-ui/react";
import {
  FaTh,
  FaRegCalendarAlt,
  FaLayerGroup,
  FaClipboardList,
  FaUserFriends,
  FaBlog,
  FaEnvelope,
  FaCog,
  FaCreditCard,
  FaSignOutAlt,
  FaFire,
  FaUserAlt,
  FaLeaf,
  FaDumbbell,
  FaUsers,
  FaUser,
} from "react-icons/fa";
import ServicesTable from "./admin/ServicesTable";
import ServiceTypeModal, { ServiceKind } from "./admin/ServiceTypeModal";
import ServiceCreatorForm, { NewServiceData } from "./admin/ServiceCreatorForm";
import { Service } from "./admin/types";

// A coded (non-screenshot) recreation of the real Wobble admin —
// "Szolgáltatások" list plus the full two-step "Új szolgáltatás" flow
// (type picker → creator form), built from Chakra primitives so visitors
// can actually click through it instead of looking at a static picture.
// Studio name/branding is a placeholder, not a real customer's.

const NAV = [
  { label: "Főoldal", icon: FaTh },
  { label: "Naptár", icon: FaRegCalendarAlt },
  { label: "Szolgáltatások", icon: FaLayerGroup, active: true },
  { label: "Kérdőívek", icon: FaClipboardList },
  { label: "Ügyfelek", icon: FaUserFriends },
  { label: "Blog", icon: FaBlog },
  { label: "Email", icon: FaEnvelope },
  { label: "Beállítások", icon: FaCog },
  { label: "Előfizetés", icon: FaCreditCard },
];

const INITIAL_SERVICES: Service[] = [
  { id: "1", emoji: "🔥", name: "HIIT Boost", type: "Csoportos", duration: 45, maxParticipants: 8, icon: FaFire, swatch: "#F4D9C6" },
  { id: "2", emoji: "🏋️", name: "Személyi edzés", type: "Egyéni", duration: 60, maxParticipants: 1, icon: FaUserAlt, swatch: "#D9E4F5" },
  { id: "3", emoji: "🧘", name: "Mobilitás kör", type: "Csoportos", duration: 50, maxParticipants: 6, icon: FaLeaf, swatch: "#DCEFD9" },
  { id: "4", emoji: "💪", name: "Core & Forma", type: "Csoportos", duration: 30, maxParticipants: 10, icon: FaDumbbell, swatch: "#EBDCF5" },
];

type View = "list" | "type-modal" | "form";

const WobbleAdminMock = () => {
  const [services, setServices] = useState(INITIAL_SERVICES);
  const [view, setView] = useState<View>("list");
  const [kind, setKind] = useState<ServiceKind>("csoportos");

  const removeService = (id: string) => setServices((prev) => prev.filter((s) => s.id !== id));

  const saveService = (data: NewServiceData) => {
    const icon = kind === "egyeni" ? FaUser : FaUsers;
    const swatch = kind === "egyeni" ? "#D9E4F5" : "#DCEFD9";
    setServices((prev) => [
      ...prev,
      {
        id: `${Date.now()}`,
        emoji: kind === "egyeni" ? "🧑" : "👥",
        name: data.name,
        type: kind === "egyeni" ? "Egyéni" : "Csoportos",
        duration: data.duration,
        maxParticipants: data.maxParticipants,
        icon,
        swatch,
      },
    ]);
    setView("list");
  };

  return (
    <Box borderRadius="18px" overflow="hidden" border="1px solid" borderColor="chalk.400" boxShadow="0 30px 80px rgba(0, 72, 86, 0.28)" bg="white">
      {/* Browser chrome */}
      <Flex align="center" gap={2} px={3} py={2} bg="chalk.100" borderBottom="1px solid" borderColor="chalk.300">
        <Flex gap={1.5}>
          {["#FF5F57", "#FEBC2E", "#28C840"].map((c) => (
            <Box key={c} w="7px" h="7px" borderRadius="full" bg={c} />
          ))}
        </Flex>
        <Flex flex="1" justify="center">
          <Flex align="center" gap={1.5} bg="white" border="1px solid" borderColor="chalk.400" borderRadius="full" px={3} py={0.5} maxW="200px" w="100%">
            <Text fontSize="7px" color="slate.500" noOfLines={1}>
              app.wobble.hu/services
            </Text>
          </Flex>
        </Flex>
        <Box w="7px" />
      </Flex>

      <Flex align="stretch">
        {/* Sidebar */}
        <Flex
          direction="column"
          w={{ base: "78px", sm: "150px" }}
          flexShrink={0}
          bg="chalk.50"
          borderRight="1px solid"
          borderColor="chalk.300"
          px={{ base: 2, sm: 3.5 }}
          py={4}
        >
          <Flex direction="column" align="center" mb={4} display={{ base: "none", sm: "flex" }}>
            <Flex
              w="44px"
              h="44px"
              borderRadius="full"
              bg="wobble.600"
              color="white"
              align="center"
              justify="center"
              fontSize="12px"
              fontWeight="800"
              mb={1.5}
              border="2px solid"
              borderColor="wobble.200"
            >
              TS
            </Flex>
            <Text fontSize="9px" fontWeight="700" color="wobble.700" noOfLines={1}>
              Te Stúdiód
            </Text>
          </Flex>

          <Flex direction="column" gap={0.5}>
            {NAV.map((item) => (
              <Flex
                key={item.label}
                align="center"
                justify={{ base: "center", sm: "flex-start" }}
                gap={2}
                px={{ base: 0, sm: 2 }}
                py={1.5}
                borderRadius="full"
                fontSize="9px"
                fontWeight={item.active ? "700" : "500"}
                color={item.active ? "white" : "wobble.700"}
                bg={item.active ? "wobble.600" : "transparent"}
              >
                <Icon as={item.icon} boxSize="9px" flexShrink={0} />
                <Text noOfLines={1} display={{ base: "none", sm: "block" }}>
                  {item.label}
                </Text>
              </Flex>
            ))}
          </Flex>

          <Box flex="1" />

          <Flex
            align="center"
            justify={{ base: "center", sm: "flex-start" }}
            gap={2}
            px={{ base: 0, sm: 2.5 }}
            py={1.5}
            borderRadius="full"
            bg="wobble.600"
            color="white"
            fontSize="9px"
            fontWeight="700"
            mb={2.5}
          >
            <Icon as={FaSignOutAlt} boxSize="9px" />
            <Text display={{ base: "none", sm: "block" }}>Kijelentkezés</Text>
          </Flex>
          <Text fontFamily="'Lato', sans-serif" fontWeight="800" fontStyle="italic" fontSize={{ base: "xs", sm: "sm" }} color="wobble.600" textAlign={{ base: "center", sm: "left" }}>
            wobble
          </Text>
        </Flex>

        {/* Content — fixed-height "viewport" so the collage layout never
            shifts; the list/form scroll inside it, while the type-picker
            overlay sits on a separate non-scrolling layer so it always
            stays centered regardless of scroll position. */}
        <Box flex="1" minW={0} position="relative" h={{ base: "420px", sm: "460px", lg: "480px" }} bg="chalk.50">
          <Box h="100%" overflowY="auto" p={{ base: 3, sm: 4 }}>
            {view === "form" ? (
              <ServiceCreatorForm kind={kind} onCancel={() => setView("list")} onSave={saveService} />
            ) : (
              <ServicesTable services={services} onDelete={removeService} onAddClick={() => setView("type-modal")} />
            )}
          </Box>

          {view === "type-modal" && (
            <ServiceTypeModal
              onCancel={() => setView("list")}
              onContinue={(picked) => {
                setKind(picked);
                setView("form");
              }}
            />
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default WobbleAdminMock;
